import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import { BackButton, EditButton } from "../../../../Components/Utils/Buttons";
import { NormalHeading } from "../../../../Components/Utils/Heading";
import NuDate from "../../../../Components/Utils/NuDate";
import NuAmount from "../../../../Components/Utils/NuAmount";
import axiosInstance from "../../../../Services/axiosCommon";
import { useToast } from "../../../../Components/Others/ToastServices";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GetAccess, GetRole } from "../../../../Components/Utils/roles";
import ViewIcon from "../../../../Components/Icons/ViewIcon";

const ViewShippingComparison = () => {
  const { comparisonId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const pageIndex = location.state?.pageIndex;
  const searchQuery = location.state?.searchQuery;

  const canEdit =
    GetAccess("shipping_comparison_edit") ||
    GetAccess("shipping_comparison_view") ||
    GetRole();
  const isAdmin = GetRole();

  const [isUpdatingSelection, setIsUpdatingSelection] = useState(false);

  // Fetch comparison details
  const { data, isLoading: dataLoading, refetch } = useQuery({
    queryKey: ["singleShippingComparison", comparisonId],
    queryFn: async () => {
      const res = await axiosInstance.get(`/shipping-comparison/${comparisonId}`);
      return res.data?.data || res.data;
    },
    enabled: !!comparisonId,
  });

  const handleSelectAgent = async (agent) => {
    const targetId = agent?.id;
    if (!targetId) {
      toast.open({
        type: "warning",
        message: "Warning",
        description: "Agent ID not found for selection.",
      });
      return;
    }

    setIsUpdatingSelection(true);
    try {
      const res = await axiosInstance.put(
        `/shipping-comparison/select-agent/${targetId}`,
        { isSelected: !agent?.isSelected }
      );

      if (res?.data?.status || res?.status === 200) {
        toast.open({
          type: "success",
          message: "Success",
          description: !agent?.isSelected
            ? `${agent?.agentName || "Agent"} selected successfully.`
            : `${agent?.agentName || "Agent"} unselected.`,
        });
        refetch();
        queryClient.invalidateQueries(["allShippingComparisons"]);
      } else {
        toast.open({
          type: "error",
          message: "Error",
          description: res?.data?.message || "Failed to update agent selection.",
        });
      }
    } catch (err) {
      console.error("Error selecting agent:", err);
      toast.open({
        type: "error",
        message: "Error",
        description:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to update agent selection.",
      });
    } finally {
      setIsUpdatingSelection(false);
    }
  };

  const agents = Array.isArray(data?.agents)
    ? data.agents
    : Array.isArray(data?.agents_detail)
      ? data.agents_detail
      : [];

  const validPrices = agents
    .map((a) => Number(a?.price))
    .filter((p) => !isNaN(p) && p > 0);

  const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : 0;
  const maxPrice = validPrices.length > 0 ? Math.max(...validPrices) : 0;
  const hasYpr = Boolean(
    data?.prId ||
    data?.prRef ||
    data?.pr_id ||
    data?.prNumber ||
    data?.pr?.id ||
    data?.pr?.ref ||
    data?.pr?.prNumber
  );
  const hasPo = Boolean(
    data?.poId ||
    data?.poRef ||
    data?.po_id ||
    data?.poNumber ||
    data?.poNo ||
    data?.po?.id ||
    data?.po?.ref ||
    data?.po?.poNumber
  );
  const hasYprAndPo = hasYpr && hasPo;

  return (
    <div className="w-full h-full p-2 lg:px-4">
      {/* Top Header */}
      <div className="w-full flex flex-row justify-between items-center gap-2 mb-3">
        <Breadcrumb
          items={[
            {
              name: "Shipping",
              link: "/shipping/shipping-comparison",
            },
            {
              name: "Shipping Comparison",
              link: "/shipping/shipping-comparison",
            },
            { name: "View Shipping Comparison" },
          ]}
        />

        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to="/shipping/shipping-comparison"
            state={{ currentPage: pageIndex, searchQuery: searchQuery || "" }}
          >
            <BackButton link={true} />
          </Link>
          {canEdit && (
            <Link
              to={`/shipping/shipping-comparison/edit/${comparisonId}`}
              state={{ data, pageIndex, searchQuery }}
            >
              <EditButton height="8" />
            </Link>
          )}
        </div>
      </div>

      {/* Main Container */}
      <div className="w-full mx-auto min-h-[calc(100vh-145px)] h-auto bg-white rounded-md overflow-y-auto p-2 sm:p-4 mt-4">
        {dataLoading && !data ? (
          <div className="w-full h-[40vh] flex justify-center items-center">
            <div className="flex flex-row gap-2">
              <div className="w-3 h-3 rounded-full bg-baseColor-primary animate-bounce" />
              <div className="w-3 h-3 rounded-full bg-baseColor-primary animate-bounce [animation-delay:-.3s]" />
              <div className="w-3 h-3 rounded-full bg-baseColor-primary animate-bounce [animation-delay:-.5s]" />
              <div className="w-3 h-3 rounded-full bg-baseColor-primary animate-bounce [animation-delay:-.7s]" />
            </div>
          </div>
        ) : (
          <>
            <NormalHeading title={data?.description || `Comparison #${comparisonId}`} />

            {/* Comparison Details List */}
            <div className="flex flex-col md:flex-row gap-4 mt-2">
              <div className="w-full md:w-1/2 flex">
                <div className="w-full rounded-[20px] bg-white py-2 flex flex-col">
                  <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                    <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">Comparison Mode</p>
                    <p className="w-1/2 py-2 pl-3 sm:pl-5 text-xs text-[#606279]">
                      {data?.prRef ? "With YPR" : "Without YPR"}
                    </p>
                  </div>

                  {data?.prRef && (
                    <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                      <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">Purchase Request (YPR)</p>
                      <p className="w-1/2 py-2 pl-3 sm:pl-5 text-xs text-[#606279] font-medium text-[#048178]">
                        {data.prRef}
                      </p>
                    </div>
                  )}

                  {data?.poRef && (
                    <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                      <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">Purchase Order (PO)</p>
                      <p className="w-1/2 py-2 pl-3 sm:pl-5 text-xs text-[#606279] font-medium text-blue-600">
                        {data.poRef}
                      </p>
                    </div>
                  )}

                  <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                    <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">Shipment Type</p>
                    <p className="w-1/2 py-2 pl-3 sm:pl-5 text-xs text-[#606279]">
                      {data?.shipmenttype || "-"}
                    </p>
                  </div>

                  <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                    <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">Port of Loading</p>
                    <p className="w-1/2 py-2 pl-3 sm:pl-5 text-xs text-[#606279]">
                      {data?.port || "-"}
                    </p>
                  </div>

                  <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                    <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">Destination Port</p>
                    <p className="w-1/2 py-2 pl-3 sm:pl-5 text-xs text-[#606279]">
                      {data?.destination || "-"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="w-full md:w-1/2 flex">
                <div className="w-full rounded-[20px] bg-white py-2 flex flex-col">
                  <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                    <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">Currency</p>
                    <p className="w-1/2 py-2 pl-3 sm:pl-5 text-xs text-[#606279]">
                      {data?.currency || "USD"}
                    </p>
                  </div>

                  <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                    <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">Created Date</p>
                    <p className="w-1/2 py-2 pl-3 sm:pl-5 text-xs text-[#606279]">
                      {data?.createdAt
                        ? NuDate({ value: data.createdAt, format: "DD-MMM-YYYY, hh:mm A" })
                        : "-"}
                    </p>
                  </div>

                  <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                    <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">Description</p>
                    <p className="w-1/2 py-2 pl-3 sm:pl-5 text-xs text-[#606279] break-words">
                      {data?.description || "-"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quoted Agents Section */}
            <div className="w-full mt-6">
              <div className="w-full">
                <p className="pb-2 pt-2 px-2 text-sm font-medium text-[#111537] underline decoration-[#048178] decoration-2 underline-offset-4">
                  Agent Quotations :
                </p>
              </div>

              <div className="w-full mt-2 overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200 rounded-md">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-[#111537]">
                      {hasYprAndPo && (
                        <th className="w-16 text-center py-2.5 px-3">Select</th>
                      )}
                      <th className={`${hasYprAndPo ? (isAdmin ? "w-[30%]" : "w-[50%]") : (isAdmin ? "w-[35%]" : "w-[55%]")} text-left py-2.5 px-4`}>
                        Agent Name
                      </th>
                      <th className={`${hasYprAndPo ? (isAdmin ? "w-[24%]" : "w-[36%]") : (isAdmin ? "w-[25%]" : "w-[45%]")} text-left py-2.5 px-4`}>
                        Quoted Price
                      </th>
                      {isAdmin && (
                        <>
                          <th className="w-[20%] text-left py-2.5 px-4">Price Revisions</th>
                          <th className="w-[20%] text-center py-2.5 px-4">History</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-xs">
                    {agents.length > 0 ? (
                      agents.map((agent, index) => {
                        const price = Number(agent?.price) || 0;
                        const isLowest = minPrice > 0 && price === minPrice;
                        const isHighest = maxPrice > 0 && price === maxPrice && minPrice !== maxPrice;
                        const historyList = Array.isArray(agent?.history) ? agent.history : [];
                        const revisionCount =
                          historyList.length > 0 ? historyList.length : agent?.revisionCount || 0;

                        return (
                          <tr
                            key={agent?.id || index}
                            className={`transition-colors ${hasYprAndPo && agent?.isSelected
                                ? "bg-emerald-50/60 font-medium text-emerald-950"
                                : "odd:bg-white even:bg-slate-50 text-[#606279]"
                              }`}
                          >
                            {/* Checkbox to select agent */}
                            {hasYprAndPo && (
                              <td className="py-3 px-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={!!agent?.isSelected}
                                  disabled={isUpdatingSelection}
                                  onChange={() => handleSelectAgent(agent)}
                                  className="w-4 h-4 text-[#048178] focus:ring-[#048178] accent-[#048178] rounded cursor-pointer"
                                  title={
                                    agent?.isSelected
                                      ? "Currently Selected Agent (Click to unselect)"
                                      : "Click to select this agent"
                                  }
                                />
                              </td>
                            )}

                            {/* Agent Name */}
                            <td className="py-3 px-4 text-left font-medium">
                              <div className="flex items-center gap-2">
                                <span className={hasYprAndPo && agent?.isSelected ? "text-emerald-900 font-semibold" : "text-gray-800"}>
                                  {agent?.agentName || agent?.name || `Agent #${agent?.agentId || index + 1}`}
                                </span>
                                {hasYprAndPo && agent?.isSelected && (
                                  <span className="text-[10px] bg-emerald-100 text-emerald-800 font-semibold px-2 py-0.5 rounded-full border border-emerald-300">
                                    Selected
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Quoted Price */}
                            <td
                              className={`py-3 px-4 text-left ${price > 0 && minPrice > 0
                                  ? isLowest
                                    ? "bg-green-100"
                                    : isHighest
                                      ? "bg-red-100"
                                      : ""
                                  : ""
                                }`}
                            >
                              <div className="flex items-center gap-2">
                                <span
                                  className={`font-semibold ${isLowest
                                      ? "text-emerald-800 font-bold"
                                      : isHighest
                                        ? "text-red-800 font-bold"
                                        : "text-gray-800"
                                    }`}
                                >
                                  <NuAmount
                                    currency={data?.currency || agent?.currency || "USD"}
                                    value={price}
                                  />
                                </span>
                                {isLowest && agents.length > 1 && (
                                  <span className="text-[10px] bg-green-200 text-green-900 font-bold px-1.5 py-0.5 rounded">
                                    Lowest
                                  </span>
                                )}
                                {isHighest && agents.length > 1 && minPrice !== maxPrice && (
                                  <span className="text-[10px] bg-red-200 text-red-900 font-bold px-1.5 py-0.5 rounded">
                                    Highest
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Price Revisions (Admin Only) */}
                            {isAdmin && (
                              <td className="py-3 px-4 text-left">
                                {revisionCount > 0 ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                                    {revisionCount} {revisionCount === 1 ? "revision" : "revisions"}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-xs">No revisions</span>
                                )}
                              </td>
                            )}

                            {/* View History Button (Admin Only) */}
                            {isAdmin && (
                              <td className="py-3 px-4 text-center">
                                {revisionCount > 0 ? (
                                  <Link
                                    to={`/shipping/shipping-comparison/priceView/${comparisonId}/${agent?.id}`}
                                    state={{ comparison: data, agent }}
                                    className="inline-flex items-center justify-center p-1.5 text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded-md transition-colors"
                                    title="View Revision History"
                                  >
                                    <ViewIcon />
                                  </Link>
                                ) : (
                                  <span className="text-gray-300">-</span>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={hasYprAndPo ? (isAdmin ? 5 : 3) : (isAdmin ? 4 : 2)}
                          className="py-6 text-center text-gray-400"
                        >
                          No agent quotes found for this comparison.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ViewShippingComparison;
